import { Divider } from '@outdoorsyco/bonfire';
import querystring from 'query-string';
import React, { useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import fortWildernessAdImage from '@/assets/advertisement-tile/fort-wilderness-ad.png';
import odnAdImage from '@/assets/advertisement-tile/odn-ad.jpg';
import Fade from '@/components/common/Transition/Fade';
import {
  countActiveFilters,
  shouldTrackNoResultsViewed,
} from '@/components/route/search/AlertMessage/noResultsFilters';
import NoResultsMessage from '@/components/route/search/AlertMessage/NoResultsMessage';
import OwnerResultsMessage from '@/components/route/search/AlertMessage/OwnerResultsMessage';
import VehicleNoResultsMessage from '@/components/route/search/AlertMessage/VehicleNoResultsMessage';
import { useSearchResultsCtx } from '@/components/route/search/SearchResults/SearchResults.context';
import Heading from '@/components/switchback/Heading/Heading';
import Link from '@/components/switchback/Link/Link';
import { EListingTileVariant, ListingTile } from '@/components/ui/ListingTile';
import { HILL_COUNTRY_ODN_ID } from '@/constants/campgroundODN';
import { ESearchFilters } from '@/constants/searchFilters';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useIsLocalityPage } from '@/hooks/useIsLocalityPage';
import { useNearFortWilderness } from '@/hooks/useNearLocation';
import useQueryParams from '@/hooks/useQueryParams';
import { useRideAutoCount } from '@/hooks/useRideAutoCount';
import { useShowRideLinks } from '@/hooks/useShowRideLinks';
import { getQueryParams } from '@/redux/selectors/queryParams';
import {
  getIsLoading,
  isCampgroundSearchResult,
  TSearchResult,
} from '@/redux/selectors/search/searchResults';
import { IWishlistingEventData } from '@/services/analytics/listings/types';
import { ERideTransitionPlacement } from '@/services/analytics/ride';
import { trackSearchNoResultsViewed } from '@/services/analytics/search';
import { ERentalType } from '@/services/analytics/types';
import { MixpanelFlags } from '@/services/experiments';
import { useMixpanelFlag } from '@/services/experiments/mixpanel';
import {
  ERentalCategory,
  INearbyCampgrounds,
  INearbyCampgroundsForDelivery,
} from '@/services/types/search/rentals/id';
import { isProdDeploy } from '@/utility/isSSR';
import {
  ICampgroundTile,
  IRentalTile,
  isCampgroundTile,
  ISearchResultTile,
} from '@/utility/mapSearchResultToTile';
import { getParamAsString, isDeliveryModeFromParams } from '@/utility/queryParams';
import { meetsRideAutosFloor } from '@/utility/rideAutos';

import { AdvertisementTile } from '../AdvertisementTile/AdvertisementTile';
import { AdvertisementTileV2 } from '../AdvertisementTileV2/AdvertisementTileV2';
import PaginationContainer from '../PaginationContainer/PaginationContainer';
import { NearbyResultsCountContainer } from '../ResultsCount/ResultsCountContainer';
import { RideCrossSellTile } from '../RideCrossSellTile/RideCrossSellTile';
import { PriceTransparencyPopover } from './PriceTransparencyPopover';
import { useStationaryCamperPromo } from './useStationaryCamperPromo';

const RIDE_CARD_POSITION = 12;

type ISearchResultsElement = React.HTMLAttributes<HTMLElement>;

interface ITileProps {
  isMapOpen?: boolean;
  wishlistingEventData?: IWishlistingEventData;
  onClickRental: (rental: ISearchResultTile, index: number, quickPreview?: boolean) => void;
  onChangeImage: (rental: ISearchResultTile, index: number, nextIndex: number) => void;
}

interface IProps extends ITileProps {
  ownerName?: string;
  searchResult: TSearchResult;
  onOwnerButtonClick: () => void;
  isCampgroundTab?: boolean;
  isPromoPage?: boolean;
  isCampgroundsSearchPage?: boolean;
  isNewPromoPage?: boolean;
}

interface IResultsProps extends ITileProps {
  hasResults?: boolean;
  hasError?: boolean;
  metaTotal?: number;
  rentals?: IRentalTile[] | ICampgroundTile[];
  isCampgroundTab?: boolean;
  isPromoPage?: boolean;
  isNewPromoPage?: boolean;
  showStationaryCamperPromo?: boolean;
  shouldShowProBadge?: boolean;
  nearbyCampgroundsForDelivery?: INearbyCampgroundsForDelivery | null;
  nearbyCampgrounds?: INearbyCampgrounds | null;
  isNearby?: boolean;
  isCampgroundsSearchPage?: boolean;
  isRvDeliverySearchPage?: boolean;
}

const TilesGrid: React.FC<IResultsProps> = ({
  rentals,
  isMapOpen,
  onClickRental,
  onChangeImage,
  isPromoPage = false,
  showStationaryCamperPromo = false,
  shouldShowProBadge = false,
  nearbyCampgrounds = null,
  wishlistingEventData,
  isNearby = false,
}) => {
  const intl = useIntl();
  const { isLocalityPage } = useIsLocalityPage();

  const { isAboveXL, isAboveDesktop, isMobile } = useBreakpoint();
  const { updateHoveredId } = useSearchResultsCtx();
  const filtersFromQuery = useSelector(getQueryParams);
  const isNearFortWilderness = useNearFortWilderness();
  const rentalCategoryFromQuery =
    ((filtersFromQuery && filtersFromQuery[ESearchFilters.RENTAL_CATEGORY]) as ERentalCategory) ||
    ERentalCategory.RV;
  const isRv = rentalCategoryFromQuery === ERentalCategory.RV;

  const isDeliveryMode = isDeliveryModeFromParams(filtersFromQuery);

  const handleMouseEnter = (rental: ISearchResultTile) => updateHoveredId(rental.id || null);
  const { stationaryCamperRentalId, customRentalTitle, hasStationaryCamperInFirstSix } =
    useStationaryCamperPromo(rentals);

  const handleMouseLeave = () => updateHoveredId(null);

  const showRideLinks = useShowRideLinks();
  const deliveryStationaryFromQuery = filtersFromQuery[ESearchFilters.DELIVERY_STATIONARY];

  const showAdvertisementTile = isRv && !deliveryStationaryFromQuery;

  const isFirstPage = !Number(filtersFromQuery[ESearchFilters.PAGE_OFFSET]);
  const isRideCrossSellEligible =
    isFirstPage &&
    !isNearby &&
    isRv &&
    !isDeliveryMode &&
    showRideLinks &&
    (rentals?.length ?? 0) >= RIDE_CARD_POSITION;
  const rideAutoCount = useRideAutoCount(
    isRideCrossSellEligible
      ? (filtersFromQuery[ESearchFilters.ADDRESS] as string | undefined)
      : undefined,
    {
      from: filtersFromQuery[ESearchFilters.DATE_FROM] as string | undefined,
      to: filtersFromQuery[ESearchFilters.DATE_TO] as string | undefined,
    },
  );
  const canShowRideCrossSellCard =
    isRideCrossSellEligible && !rentals?.[0]?.loading && meetsRideAutosFloor(rideAutoCount);
  // Bucket only once the card can render, so unseen Guests don't dilute the treatment arm.
  const srpRideCrossSellEnabled = useMixpanelFlag(MixpanelFlags.SRP_RIDE_CROSS_SELL_CARD, {
    shouldRun: canShowRideCrossSellCard,
  });
  const showRideCrossSellCard = canShowRideCrossSellCard && srpRideCrossSellEnabled;
  const AdvertisementTileTarget = isAboveDesktop ? '_blank' : undefined;

  const hillCountryAdvertisement = useMemo(() => {
    if (!showAdvertisementTile) return null;

    if (nearbyCampgrounds?.data?.some(campground => campground.odn_id === HILL_COUNTRY_ODN_ID)) {
      return {
        heading: intl.formatMessage({
          defaultMessage: 'Discover luxury glamping in the heart of Texas Hill Country',
          id: 'JMP8CJ',
        }),
        linkLabel: intl.formatMessage({ defaultMessage: 'Book a stay', id: 'hakMYM' }),
        image: odnAdImage.src,
        href: '/destinations/outdoorsy-hill-country',
        odn_id: HILL_COUNTRY_ODN_ID,
        target: AdvertisementTileTarget,
      };
    }
  }, [AdvertisementTileTarget, intl, nearbyCampgrounds?.data, showAdvertisementTile]);

  const fortWildernessAdvertisement = useMemo(() => {
    if (!showAdvertisementTile || !isNearFortWilderness || isPromoPage || isProdDeploy())
      return null;

    return {
      heading: intl.formatMessage(
        {
          defaultMessage: 'Get an RV delivered to Disney’s {br} Fort Wilderness Resort',
          id: 'WdnFjN',
        },
        { br: <br /> },
      ),
      linkLabel: intl.formatMessage({ defaultMessage: 'Learn more ', id: 'cQ/nNz' }),
      image: fortWildernessAdImage.src,
      gradient: true,
      href: '/promos/rv-delivery/fort-wilderness',
      target: AdvertisementTileTarget,
    };
  }, [AdvertisementTileTarget, intl, isNearFortWilderness, isPromoPage, showAdvertisementTile]);

  // Ad tiles render ahead of the rentals, so shift the insert point to keep the card 12th in the grid.
  const adTileCount = Number(!!hillCountryAdvertisement) + Number(!!fortWildernessAdvertisement);
  const rideCardAfterIndex = RIDE_CARD_POSITION - 2 - adTileCount;

  if (!rentals?.length) return null;

  return (
    <ul
      className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 mb-11 lg:mb-12"
      data-map-open={String(isMapOpen || false)}>
      {hillCountryAdvertisement && <AdvertisementTile {...hillCountryAdvertisement} />}
      {fortWildernessAdvertisement && <AdvertisementTileV2 {...fortWildernessAdvertisement} />}
      {rentals?.map((tile, index) => {
        const displayStationaryCamperPromo =
          tile.id === stationaryCamperRentalId &&
          hasStationaryCamperInFirstSix &&
          showStationaryCamperPromo;

        const tileWidget = isCampgroundTile(tile) ? (
          <ListingTile
            rentalTile={tile}
            onSlideChange={nextIndex => onChangeImage(tile, index, nextIndex)}
            target={isAboveDesktop ? '_blank' : undefined}
            onClick={() => onClickRental(tile, index)}
            onMouseEnter={() => handleMouseEnter(tile)}
            onMouseLeave={() => handleMouseLeave()}
            withPriority={!isMobile || (isMobile && index < 2)}
            withFetchPriority={isMobile && index < 2 ? 'high' : undefined}
            ssrNavigation={false}
            wishlistingEventData={{ index, ...wishlistingEventData }}
          />
        ) : (
          <ListingTile
            rentalTile={tile}
            onSlideChange={nextIndex => onChangeImage(tile, index, nextIndex)}
            target={isAboveDesktop ? '_blank' : undefined}
            onClick={() => onClickRental(tile, index)}
            onMouseEnter={() => handleMouseEnter(tile)}
            onMouseLeave={() => handleMouseLeave()}
            dataTestId="search-result"
            displayStationaryCamperPromo={displayStationaryCamperPromo}
            customRentalTitle={customRentalTitle || tile.title}
            variant={EListingTileVariant.Vertical}
            showSuperhost
            showMilitaryMember
            showPartnerHost
            showGuestFavorite
            showProBadge={shouldShowProBadge && tile.dealer}
            withPriority={!isMobile || (isMobile && index < 2)}
            withFetchPriority={isMobile && index < 2 ? 'high' : undefined}
            ssrNavigation={false}
            wishlistingEventData={{ index, ...wishlistingEventData }}
            showQuickPreview
            quickPreviewOnClickViewListing={() => onClickRental(tile, index, true)}
          />
        );

        return (
          <React.Fragment key={tile.id || index}>
            <li>
              {/*
                Performance Note:
                Wrapping tiles in <Fade /> can delay visual feedback on page load, especially under throttled conditions.
                This animation has been linked to increased INP scores, particularly on locality pages.
                To improve perceived performance and reduce INP, avoid using <Fade /> when possible—especially on initial render.
              */}
              {!isLocalityPage ? <Fade duration={150}>{tileWidget}</Fade> : tileWidget}
              {index === 0 && !tile.loading && !isNearby && !isDeliveryMode && (
                <PriceTransparencyPopover isMapOpen={isMapOpen && !isAboveXL} />
              )}
            </li>
            {showRideCrossSellCard && index === rideCardAfterIndex && (
              <RideCrossSellTile
                placement={ERideTransitionPlacement.SRP_RIDE_CROSS_SELL_POSITION_12}
              />
            )}
          </React.Fragment>
        );
      })}
    </ul>
  );
};

export const NoSearchResults: React.FC<IResultsProps> = ({
  hasResults,
  hasError,
  metaTotal,
  isMapOpen,
  rentals,
  onClickRental,
  onChangeImage,
  isCampgroundTab,
  isCampgroundsSearchPage,
  isPromoPage,
  isNewPromoPage,
}) => {
  const intl = useIntl();
  const queryParams = useQueryParams();
  const reduxQueryParams = useSelector(getQueryParams);
  const isLoading = useSelector(getIsLoading);
  const showMoreRVsText = intl.formatMessage({
    defaultMessage: 'Show more RVs',
    id: 'hsUwP6',
  });

  const pageOffset = Number(reduxQueryParams?.[ESearchFilters.PAGE_OFFSET]) || 0;
  const trackNoResults = shouldTrackNoResultsViewed({
    isLoading,
    hasResults: !!hasResults,
    hasError: !!hasError,
    pageOffset,
    metaTotal,
    isPromoPage,
    isNewPromoPage,
  });

  useEffect(() => {
    if (trackNoResults) {
      const hasDates = Boolean(
        reduxQueryParams[ESearchFilters.DATE_FROM] || reduxQueryParams[ESearchFilters.DATE_TO],
      );
      const isDeliveryMode = getParamAsString(reduxQueryParams[ESearchFilters.DELIVERY]) === 'true';
      const activeFilterCount = countActiveFilters(reduxQueryParams);
      trackSearchNoResultsViewed({
        rentalType:
          isCampgroundTab || isCampgroundsSearchPage ? ERentalType.CAMPGROUND : ERentalType.RV,
        activeFilterCount,
        hasDates,
        isDeliveryMode,
        searchQuery: getParamAsString(reduxQueryParams[ESearchFilters.ADDRESS]) || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showMoreDeliverableRVsText = intl.formatMessage({
    defaultMessage: 'Show more deliverable RVs',
    id: '43hBvv',
  });

  const similarQuery = {
    [ESearchFilters.ADDRESS]: queryParams[ESearchFilters.ADDRESS],
    [ESearchFilters.DELIVERY_ADDRESS]: queryParams[ESearchFilters.DELIVERY_ADDRESS],
    [ESearchFilters.BOUNDS_NE]: queryParams[ESearchFilters.BOUNDS_NE],
    [ESearchFilters.BOUNDS_SW]: queryParams[ESearchFilters.BOUNDS_SW],
  };

  const ShowMoreRVsLink = !rentals?.length ? null : (
    <Link href={`/rv-search?${querystring.stringify(similarQuery)}`} className="block mb-7">
      <span className="text-base font-medium autoType500">
        {isCampgroundTab ? showMoreDeliverableRVsText : showMoreRVsText}
      </span>
    </Link>
  );

  const NoResultsMsg = hasResults ? null : isCampgroundTab ? (
    <NoResultsMessage hasNearby={!!rentals?.length} isCampground={isCampgroundTab} />
  ) : (
    <VehicleNoResultsMessage hasNearby={!!rentals?.length} />
  );

  const NearbyResultsHeading =
    !!rentals?.length &&
    (!isCampgroundTab ? (
      <>
        <Divider />
        <div className="flex">
          <Heading level={2} className="mt-10 mb-3 highlight autoType600 md:block">
            <FormattedMessage
              defaultMessage="Here are some results that don’t quite match your search criteria"
              id="rNwwki"
              description="Title for nearby listings on search page"
            />
          </Heading>
          <div className="mt-10 mb-3 md:ml-auto">
            <NearbyResultsCountContainer />
          </div>
        </div>
      </>
    ) : (
      <>
        <Heading
          level={2}
          className="mb-4 text-2xl font-medium border-t mt-9 pt-9 border-neutral-20">
          <FormattedMessage
            defaultMessage="Other travel options"
            id="kSKNv4"
            description="Title for nearby listings on search page"
          />
        </Heading>
        <div className="mb-10">
          <p className="text-justify leading-7">
            <FormattedMessage
              defaultMessage={`A delivered RV is a lot like a Stay. You don’t have to drive or tow it. Instead, let your host deliver and set up the vehicle at a campsite you’ve reserved — or a friend’s back yard you’ve called dibs on.`}
              id="0+IcAH"
            />
          </p>
        </div>
      </>
    ));

  const NearbyResultsTileGrid = (
    <TilesGrid
      isMapOpen={isMapOpen}
      rentals={rentals}
      onClickRental={onClickRental}
      onChangeImage={onChangeImage}
      isNearby
    />
  );

  return (
    <>
      {NoResultsMsg}
      {!isCampgroundsSearchPage && NearbyResultsHeading}
      {!isCampgroundsSearchPage && NearbyResultsTileGrid}
      {!isCampgroundsSearchPage && ShowMoreRVsLink}
    </>
  );
};

const SearchResults: React.FC<IProps & ISearchResultsElement> = ({
  searchResult,
  isMapOpen,
  onOwnerButtonClick,
  ownerName,
  onClickRental,
  onChangeImage,
  isCampgroundTab,
  isPromoPage,
  wishlistingEventData,
  isCampgroundsSearchPage,
  isNewPromoPage,
}) => {
  const isLoading = useSelector(getIsLoading);
  const loadingTiles: IRentalTile[] = Array(24).fill({ loading: true });
  const shouldShowProBadge = !isPromoPage;
  const searchResultData = isCampgroundSearchResult(searchResult)
    ? searchResult.campgrounds.data
    : searchResult.data;
  const nearbyCampgrounds = searchResult.nearbyCampgrounds;

  return (
    <>
      {!!ownerName && !isCampgroundTab && !isNewPromoPage && (
        <OwnerResultsMessage ownerName={ownerName} onOwnerButtonClick={onOwnerButtonClick} />
      )}

      {isLoading ? (
        <TilesGrid
          rentals={loadingTiles}
          isMapOpen={isMapOpen}
          onClickRental={onClickRental}
          onChangeImage={onChangeImage}
          isPromoPage={isPromoPage}
          wishlistingEventData={wishlistingEventData}
        />
      ) : (
        <>
          <TilesGrid
            rentals={searchResultData}
            isMapOpen={isMapOpen}
            onClickRental={onClickRental}
            onChangeImage={onChangeImage}
            isPromoPage={isPromoPage}
            nearbyCampgrounds={nearbyCampgrounds}
            shouldShowProBadge={shouldShowProBadge}
            showStationaryCamperPromo
            wishlistingEventData={wishlistingEventData}
          />
          {!!searchResult && !isPromoPage && <PaginationContainer />}
          <NoSearchResults
            hasResults={!!searchResultData.length}
            hasError={!!searchResult.error}
            metaTotal={searchResult.meta?.total}
            rentals={
              searchResultData.length <= 8 && !isNewPromoPage ? searchResult?.nearby?.data : []
            }
            isMapOpen={isMapOpen}
            isCampgroundTab={isCampgroundTab}
            isCampgroundsSearchPage={isCampgroundsSearchPage}
            isPromoPage={isPromoPage}
            isNewPromoPage={isNewPromoPage}
            onClickRental={onClickRental}
            onChangeImage={onChangeImage}
          />
        </>
      )}
    </>
  );
};

export default SearchResults;
